import React, { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DialogBase from '../../../base/DialogBase/DialogBase';
import styles from './RegisterContentsDialog.module.css';
import { ContentSortInput, Tag, useGetUnregisteredContentsQuery, useGetRegisteredContentsQuery, useRegisterContentsMutation, useUnregisterContentsMutation } from '../../../generated/graphql';
import DataTableNew from '../../main/DataTableNew/DataTableNew';
import RefreshButton from '../../main/RefreshButton/RefreshButton';
import { CheckSquare, XCircle, Plus, Minus, ArrowRight, Trash2, RefreshCw } from 'lucide-react';
import { createGraphQLOrder, TableSort } from '../../main/DataTableNew/DataTableSorting';

type ViewMode = 'unregistered' | 'registered' | 'all';

type RegisterContentsDialogProps = {
    onClose: () => void,
    onCloseAndUpdateCache?: () => void,
    onRemove?: () => void,
    clientId: string,
    libraryId: string,
    visible: boolean,
    sourceLibraryId?: string,
    sourceLibraryName?: string,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const RegisterContentsDialog = (props: RegisterContentsDialogProps) => {
    const { style, visible, clientId, libraryId, sourceLibraryId, sourceLibraryName, onClose, onCloseAndUpdateCache, onRemove } = props;
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('unregistered');
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const [registerContents, registerContentsResult] = useRegisterContentsMutation();
    const [unregisterContents, unregisterContentsResult] = useUnregisterContentsMutation();

    const unregisteredContents = useGetUnregisteredContentsQuery({
        variables: {
            id: libraryId,
            contains: searchText,
            category: searchText,
            tagIds: tagIds,
            order: createGraphQLOrder<ContentSortInput>(sort),
        }
    });

    const registeredContents = useGetRegisteredContentsQuery({
        variables: {
            id: libraryId,
            contains: searchText,
            category: searchText,
            tagIds: tagIds,
            order: createGraphQLOrder<ContentSortInput>(sort),
        }
    });

    const refetchAll = () => {
        unregisteredContents.refetch();
        registeredContents.refetch();
    };

    const filteredUnregistered = useMemo(() => {
        const nodes = unregisteredContents.data?.Library?.unregisteredContents?.nodes as any[] | undefined;
        if (!nodes) return [];
        return sourceLibraryId ? nodes.filter((n: any) => n?.libraryId === sourceLibraryId) : nodes;
    }, [unregisteredContents.data, sourceLibraryId]);

    const filteredRegistered = useMemo(() => {
        const nodes = registeredContents.data?.Library?.registeredContents?.nodes as any[] | undefined;
        if (!nodes) return [];
        return sourceLibraryId ? nodes.filter((n: any) => n?.libraryId === sourceLibraryId) : nodes;
    }, [registeredContents.data, sourceLibraryId]);

    const displayData = useMemo(() => {
        if (viewMode === 'unregistered') return filteredUnregistered.map((n: any) => ({ ...n, _registered: false }));
        if (viewMode === 'registered') return filteredRegistered.map((n: any) => ({ ...n, _registered: true }));
        const registeredIds = new Set(filteredRegistered.map((n: any) => n?.id));
        return [
            ...filteredRegistered.map((n: any) => ({ ...n, _registered: true })),
            ...filteredUnregistered.filter((n: any) => !registeredIds.has(n?.id)).map((n: any) => ({ ...n, _registered: false })),
        ];
    }, [viewMode, filteredUnregistered, filteredRegistered]);

    const handleRegisterItems = (ids: string[]) => {
        setError(null);
        registerContents({ variables: { id: libraryId, contentIds: ids } })
            .then(() => {
                setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
                refetchAll();
            })
            .catch(err => setError(err?.message || "Failed to register contents."));
    };

    const handleUnregisterItems = (ids: string[]) => {
        setError(null);
        unregisterContents({ variables: { id: libraryId, ids } })
            .then(() => {
                setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
                refetchAll();
            })
            .catch(err => setError(err?.message || "Failed to unregister contents."));
    };

    const handleRegisterUpdates = () => {
        // Find unregistered items whose name matches a registered item's name
        const registeredNames = new Set(
            filteredRegistered.map((n: any) => (n?.name ?? '').toLowerCase().trim()).filter(Boolean)
        );
        const matchingIds = filteredUnregistered
            .filter((n: any) => {
                const name = (n?.name ?? '').toLowerCase().trim();
                return name && registeredNames.has(name);
            })
            .map((n: any) => n.id as string);

        if (matchingIds.length === 0) {
            setError("No unregistered content found with names matching already-registered content.");
            return;
        }
        handleRegisterItems(matchingIds);
    };

    const handleFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode);
        setSelectedIds([]);
    };

    const handleClose = () => {
        setError(null);
        setSelectedIds([]);
        setSearchText("");
        setTagIds([]);
        setViewMode('unregistered');
        onClose();
    };

    const hasSelection = selectedIds.length > 0;
    const isLoading = unregisteredContents.loading || registeredContents.loading || registerContentsResult.loading || unregisterContentsResult.loading;

    const registeredIdSet = useMemo(() => new Set(filteredRegistered.map((n: any) => n?.id)), [filteredRegistered]);

    const selectedRegisteredIds = useMemo(() =>
        selectedIds.filter(id => registeredIdSet.has(id)),
        [selectedIds, registeredIdSet]
    );

    const selectedUnregisteredIds = useMemo(() =>
        selectedIds.filter(id => !registeredIdSet.has(id)),
        [selectedIds, registeredIdSet]
    );

    const RowActionButton = ({ row }: { row: any }) => {
        const isRegistered = row._registered;
        return (
            <button
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors"
                style={{
                    color: isRegistered ? 'var(--danger)' : 'var(--accent)',
                    backgroundColor: isRegistered ? 'var(--danger-soft, rgba(239,68,68,0.08))' : 'var(--accent-soft)',
                    border: 'none',
                    cursor: 'pointer',
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isRegistered) {
                        handleUnregisterItems([row.id]);
                    } else {
                        handleRegisterItems([row.id]);
                    }
                }}
                title={isRegistered ? 'Unregister this content' : 'Register this content'}
            >
                {isRegistered ? <Minus size={12} /> : <Plus size={12} />}
                {isRegistered ? 'Unregister' : 'Register'}
            </button>
        );
    };

    return (
        <DialogBase
            title="Manage Content Registration"
            type="Custom"
            visible={visible}
            onClose={handleClose}
            isLoading={isLoading}
            width="1200px"
            className={styles.RegisterContentsDialog}
            style={style}
            headerActions={
                sourceLibraryId ? (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-ghost text-sm flex items-center gap-1.5"
                            title={`Go to ${sourceLibraryName || 'source library'}`}
                            onClick={() => { handleClose(); navigate(`/clients/${clientId}/library/${sourceLibraryId}`, { state: { tab: 'direct-contents' } }); }}
                        >
                            <ArrowRight size={14} /> Open Library
                        </button>
                        {onRemove && (
                            <button
                                className="btn btn-ghost text-sm flex items-center gap-1.5"
                                style={{ color: 'var(--danger)' }}
                                title="Remove this source library"
                                onClick={() => { handleClose(); onRemove(); }}
                            >
                                <Trash2 size={14} /> Remove
                            </button>
                        )}
                    </div>
                ) : undefined
            }
            actions={
                <>
                    <button className="btn btn-secondary" onClick={handleClose}>
                        Close
                    </button>
                    {onCloseAndUpdateCache && (
                        <button className="btn btn-primary" onClick={() => { handleClose(); onCloseAndUpdateCache(); }}>
                            Close & Update Cache
                        </button>
                    )}
                </>
            }
            data-testid="RegisterContentsDialog">

            {error && (
                <div className={styles.errorBanner}>
                    <span>{error}</span>
                </div>
            )}

            {(unregisteredContents.error || registeredContents.error) && (
                <div className={styles.errorBanner}>
                    <span>Failed to load contents. Please try refreshing.</span>
                </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", height: "560px" }}>
                {/* Toolbar */}
                <div className={styles.toolbar}>
                    <div className={styles.toolbarActions}>
                        <RefreshButton
                            onRefresh={refetchAll}
                            className="btn btn-secondary inline-flex items-center gap-1.5"
                            iconSize={14}
                        />
                        {/* View mode toggle */}
                        <div className="flex gap-0 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {([
                                { mode: 'unregistered' as ViewMode, label: 'Unregistered', count: filteredUnregistered.length },
                                { mode: 'registered' as ViewMode, label: 'Registered', count: filteredRegistered.length },
                                { mode: 'all' as ViewMode, label: 'All', count: filteredUnregistered.length + filteredRegistered.length },
                            ]).map(({ mode, label, count }) => (
                                <button
                                    key={mode}
                                    onClick={() => handleViewModeChange(mode)}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: viewMode === mode ? 'var(--accent)' : 'transparent',
                                        color: viewMode === mode ? 'white' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        borderRight: mode !== 'all' ? '1px solid var(--border-default)' : undefined,
                                    }}
                                >
                                    {label} ({count})
                                </button>
                            ))}
                        </div>
                        {filteredUnregistered.length > 0 && (
                            <button
                                onClick={() => handleRegisterItems(filteredUnregistered.map((n: any) => n.id))}
                                className="inline-flex items-center gap-1 text-xs font-medium rounded-md px-2.5 py-1.5 transition-colors"
                                style={{
                                    color: 'var(--accent)',
                                    backgroundColor: 'var(--accent-soft)',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                title={`Register all ${filteredUnregistered.length} unregistered item(s)`}
                            >
                                <Plus size={12} />
                                Register All ({filteredUnregistered.length})
                            </button>
                        )}
                        {filteredUnregistered.length > 0 && filteredRegistered.length > 0 && (
                            <button
                                onClick={handleRegisterUpdates}
                                className="inline-flex items-center gap-1 text-xs font-medium rounded-md px-2.5 py-1.5 transition-colors"
                                style={{
                                    color: 'var(--success, #15803d)',
                                    backgroundColor: 'var(--success-soft, rgba(34,197,94,0.1))',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                title="Register unregistered content whose name matches already-registered content"
                            >
                                <RefreshCw size={12} />
                                Register Updates
                            </button>
                        )}
                    </div>
                    {hasSelection && (
                        <div className={styles.selectionCount}>
                            <CheckSquare size={14} />
                            <span className={styles.selectionCountBadge}>
                                {selectedIds.length}
                            </span>
                            <span>selected</span>
                            {selectedUnregisteredIds.length > 0 && (
                                <button
                                    onClick={() => handleRegisterItems(selectedUnregisteredIds)}
                                    className="inline-flex items-center gap-1 ml-1 text-xs font-medium rounded-md px-2 py-1 transition-colors"
                                    style={{
                                        color: 'var(--accent)',
                                        backgroundColor: 'var(--accent-soft)',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Plus size={12} />
                                    Register {selectedUnregisteredIds.length}
                                </button>
                            )}
                            {selectedRegisteredIds.length > 0 && (
                                <button
                                    onClick={() => handleUnregisterItems(selectedRegisteredIds)}
                                    className="inline-flex items-center gap-1 ml-1 text-xs font-medium rounded-md px-2 py-1 transition-colors"
                                    style={{
                                        color: 'var(--danger)',
                                        backgroundColor: 'var(--danger-soft, rgba(239,68,68,0.08))',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Minus size={12} />
                                    Unregister {selectedRegisteredIds.length}
                                </button>
                            )}
                            <button
                                onClick={() => setSelectedIds([])}
                                className="inline-flex items-center gap-1 ml-1 text-xs font-medium rounded-md px-2 py-1 transition-colors"
                                style={{
                                    color: 'var(--text-tertiary)',
                                    background: 'transparent',
                                    border: '1px solid var(--border-default)',
                                    cursor: 'pointer',
                                }}
                                title="Clear selection"
                            >
                                <XCircle size={12} />
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Content table */}
                <DataTableNew
                    {...(viewMode === 'all'
                        ? { sortingMode: 'none' as const }
                        : { sortingMode: 'server' as const, sort, onSortChange: setSort })}
                    searchable
                    filterable
                    selectable
                    showCheckboxes
                    controlledSelectedIds={selectedIds}
                    showName
                    showTags
                    showCreatedBy
                    showCreatedDate
                    onFilterChanged={handleFilterChanged}
                    onSearchChanged={(value) => setSearchText(value)}
                    onSelected={(value) => setSelectedIds(value)}
                    fetchMore={() => {
                        if (viewMode === 'unregistered' || viewMode === 'all') {
                            unregisteredContents.data?.Library?.unregisteredContents?.pageInfo?.hasNextPage && unregisteredContents.fetchMore({
                                variables: {
                                    after: unregisteredContents.data?.Library?.unregisteredContents?.pageInfo.endCursor,
                                    order: createGraphQLOrder<ContentSortInput>(sort),
                                },
                                updateQuery: (previousResult, { fetchMoreResult }) => {
                                    if (!fetchMoreResult) return previousResult;
                                    const previousContents = unregisteredContents.data?.Library?.unregisteredContents?.nodes || [];
                                    const newContents = fetchMoreResult.Library?.unregisteredContents?.nodes || [];
                                    return {
                                        Library: {
                                            id: unregisteredContents.data?.Library?.id,
                                            unregisteredContents: {
                                                nodes: [...previousContents, ...newContents],
                                                pageInfo: fetchMoreResult.Library?.unregisteredContents?.pageInfo as any
                                            }
                                        }
                                    };
                                }
                            });
                        }
                        if (viewMode === 'registered' || viewMode === 'all') {
                            registeredContents.data?.Library?.registeredContents?.pageInfo?.hasNextPage && registeredContents.fetchMore({
                                variables: {
                                    after: registeredContents.data?.Library?.registeredContents?.pageInfo.endCursor,
                                    order: createGraphQLOrder<ContentSortInput>(sort),
                                },
                                updateQuery: (previousResult, { fetchMoreResult }) => {
                                    if (!fetchMoreResult) return previousResult;
                                    const previousContents = registeredContents.data?.Library?.registeredContents?.nodes || [];
                                    const newContents = fetchMoreResult.Library?.registeredContents?.nodes || [];
                                    return {
                                        Library: {
                                            id: registeredContents.data?.Library?.id,
                                            registeredContents: {
                                                nodes: [...previousContents, ...newContents],
                                                pageInfo: fetchMoreResult.Library?.registeredContents?.pageInfo as any
                                            }
                                        }
                                    };
                                }
                            });
                        }
                    }}
                    isLoading={unregisteredContents.loading || registeredContents.loading}
                    data={displayData}
                    columns={[
                        { id: "library.name", name: "Library" },
                        { id: "category", name: "Category", sortField: 'category' },
                        ...(viewMode === 'all' ? [{
                            id: '_status',
                            name: 'Status',
                            width: '110px',
                            render: (row: any) => (
                                <span
                                    className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
                                    style={{
                                        color: row._registered ? 'var(--success)' : 'var(--text-tertiary)',
                                        backgroundColor: row._registered ? 'var(--success-soft, rgba(34,197,94,0.1))' : 'var(--surface-100)',
                                    }}
                                >
                                    {row._registered ? 'Registered' : 'Unregistered'}
                                </span>
                            )
                        }] : []),
                        {
                            id: '_action',
                            name: '',
                            width: '110px',
                            render: (row: any) => <RowActionButton row={row} />
                        }
                    ]}
                    emptyTitle={viewMode === 'registered' ? "No registered content found" : "No unregistered content found"}
                    emptyDescription={
                        searchText || tagIds.length > 0
                            ? "No content matches your current filters. Try adjusting your search or removing tag filters."
                            : viewMode === 'registered'
                                ? "No content from this library has been registered yet."
                                : "All available content in this library has already been registered."
                    }
                />
            </div>
        </DialogBase>
    );
};

export default RegisterContentsDialog;
